import { Box, useColorModeValue, useToast } from "@chakra-ui/react";
import React from "react";
import { QuoteApi } from "../api/QuoteApi";
import ComboBox from "../components/form-fields/ComboBox";
import { allTableColumns } from "../components/quotes/QuotesTableColumns";
import QuoteTableContainer from "../components/QuoteTableContainer";
import useUser from "../hooks/useUser";
import { Quote } from "../lib/models/quote.model";
import { QuoteUtils } from "../lib/quote-utils";
import { SelectItem } from "../lib/types/SelectItem";
import { UserAccount } from "../lib/types/UserAccountType";

function QuotesView() {
  const [quotes, setQuotes] = React.useState<Quote[]>([]);
  const [accounts, setAccounts] = React.useState<UserAccount[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const { user } = useUser();
  const toast = useToast();
  const toastVariant = useColorModeValue("subtle", "solid");
  const [selectedAccount, setSelectedAccount] =
    React.useState<SelectItem | null>(null);

  const accountItems: SelectItem[] = React.useMemo(() => {
    return accounts.map((account) => {
      return {
        label: `${account.firstName} ${account.lastName}`,
        value: account.userID,
      };
    });
  }, [accounts]);

  const fetchUsers = React.useCallback(() => {
    if (!user) return;
    setLoading(true);

    QuoteApi.getQuoteUsers()
      .then((res) => {
        const activeUsers = res.data.map((x) => {
          return x;
        });
        setAccounts(activeUsers);
      })
      .catch((err) => {});
  }, [user]);

  const fetchAllQuotes = React.useCallback(() => {
    setLoading(true);

    if (!user?.email) {
      return;
    }

    QuoteApi.getQuotes({ username: user.email, allquotes: true })
      .then((res) => {
        const activeQuotes = res.data.map((x) => {
          x.quoteTypeLabel = QuoteUtils.PrettyPrintQuoteType(x.quoteType);
          x.createdBy = `${x.firstname} ${x.lastname}`;
          return x;
        });

        setQuotes(activeQuotes);
      })
      .catch((err) => {
        toast({
          title: "Unable to Load Quotes",
          description: "An error has occurred when attempting to fetch quotes",
          status: "error",
          variant: toastVariant,
          isClosable: true,
          position: "top-right",
        });
      })
      .finally(() => setLoading(false));
  }, [setQuotes, user, toast, toastVariant]);

  const fetchQuotes = React.useCallback(() => {
    setLoading(true);

    QuoteApi.getQuotesById(selectedAccount?.value)
      .then((res) => {
        const activeQuotes = res.data.map((x) => {
          x.quoteTypeLabel = QuoteUtils.PrettyPrintQuoteType(x.quoteType);
          x.createdBy = `${x.firstname} ${x.lastname}`;
          return x;
        });

        setQuotes(activeQuotes);
      })
      .catch((err) => {
        toast({
          title: "Unable to Load Quotes",
          description: "An error has occurred when attempting to fetch quotes",
          status: "error",
          variant: toastVariant,
          isClosable: true,
          position: "top-right",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setQuotes, selectedAccount, toast, toastVariant]);

  React.useEffect(() => {
    if (!selectedAccount) {
      fetchAllQuotes();
      fetchUsers();
      return;
    }
    fetchQuotes();
  }, [selectedAccount, fetchQuotes, fetchAllQuotes, fetchUsers]);

  return (
    <QuoteTableContainer
      refetchQuotes={fetchQuotes}
      loading={loading}
      title={
        selectedAccount ? `Quotes for ${selectedAccount.label}` : "All Quotes"
      }
      quotes={quotes}
      columns={
        selectedAccount
          ? allTableColumns.filter((column) => column.Header !== "Owned By")
          : allTableColumns
      }
    >
      <Box width={330} mb={4}>
        <ComboBox
          items={accountItems}
          label=""
          onSelect={setSelectedAccount}
          isDisabled={loading || !!selectedAccount}
          itemSelected={selectedAccount}
          placeholder="Select a User..."
        />
      </Box>
    </QuoteTableContainer>
  );
}

export default QuotesView;
